import { connect } from 'react-redux'
import { compose, withHandlers, withProps, withState, lifecycle, renderComponent, branch } from 'recompose'
import { bindActionCreators } from 'redux'
import { omit } from 'lodash'


import * as EndUserActions from 'app/modules/endUser/actions'
import * as EmbedActions from 'app/modules/embed/actions'
import * as EmbedSelectors from 'app/modules/embed/selectors'
import * as EndUserSelectors from 'app/modules/endUser/selectors'
import * as GeolocationSelectors from 'app/modules/geolocation/selectors'
import * as GeolocationActions from 'app/modules/geolocation/actions'
import { captureException } from 'utils/SentryHelpers'


import { Loading, Confirm, unsubscribeChoices } from './component'

const mapStateToProps = state => ({
  backgroundColor: EmbedSelectors.backgroundColor(state),
  embedLoaded: EmbedSelectors.embedLoaded(state),
  logoUrl: EmbedSelectors.logoUrl(state),
  orgId: EmbedSelectors.getOrgId(state),
  embedNotFetched: EmbedSelectors.embedNotFetched(state),
  embedLoadFailed: EmbedSelectors.embedLoadFailed(state),
  embedFetchPending: EmbedSelectors.embedFetchPending(state),
  organizationName: EmbedSelectors.getOrgName(state),
  unsubscribePending: EndUserSelectors.unsubscribePending(state),
  unsubscribed: EndUserSelectors.unsubscribed(state),
  unsubscribeError: EndUserSelectors.unsubscribeError(state),
  unsubscribeErrorMessage: EndUserSelectors.unsubscribeErrorMessage(state),
  resubscribePending: EndUserSelectors.resubscribePending(state),
  resubscribed: EndUserSelectors.resubscribed(state),
  resubscribeError: EndUserSelectors.resubscribeError(state),
  resubscribeErrorMessage: EndUserSelectors.resubscribeErrorMessage(state),
  isFetchGeolocationPending: GeolocationSelectors.isFetchGeolocationPending(state),
  isGeolocationLoaded: GeolocationSelectors.isGeolocationLoaded(state),
  // If we failed to fetch geolocation data, assume that the visitor may be
  // coming from a GDPR-applicable IP address.
  isGdprConsentRequired: GeolocationSelectors.isFetchGeolocationFailure(state) || GeolocationSelectors.getIsGdprApplicable(state),
  unsubRequestDeleteStatus: EndUserSelectors.unsubRequestDeleteStatus(state),
})


const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      unsubscribe: EndUserActions.unsubscribeById,
      unsubscribeByEncodedEmail: EndUserActions.unsubscribeByEncodedEmail,
      unsubscribeByEmailAndOrg: EndUserActions.unsubscribeByEmailAndOrgId,
      resubscribe: EndUserActions.resubscribe,
      resubscribeByEmailandOrgId: EndUserActions.resubscribeByEmailandOrgId,
      requestDeleteData: EndUserActions.requestUnsubDeleteData,
      fetchEmbedById: EmbedActions.fetchEmbedById,
      fetchGeolocation: GeolocationActions.fetchGeolocation,
    },
    dispatch
  ),
})


export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withState('responded', 'setResponded', false),
  withState('resubscribed', 'setResubscribe', false),
  withState('unsubscribed', 'setUnsubscribe', false),
  withProps(props => {
    // certain email clients html decode links (i.e. "&email=ryan" -> "&amp;email=ryan")
    // looks like it's most prevalent on Windows machines running IE
    const queryParams = props.location.query
    const params = {
      endUserId: queryParams.endUserId || queryParams['amp;endUserId'],
      // orgId might have already been loaded from the embed
      orgId: props.orgId || queryParams.orgId || queryParams['amp;orgId'],
      campaignId: queryParams.d_campaign || queryParams['amp;d_campaign'],
      email: queryParams.email || queryParams['amp;email'],
      identifier: queryParams.identifier || queryParams['amp;identifier'],
      embedId: queryParams.embedId || queryParams['amp;embedId'],
    }

    // we're removing the email from the query params and instead adding "identifier"
    if (!!params.identifier && !params.email) {
      try {
        params.email = atob(params.identifier)
      } catch(err) {
        // the email wansn't base64 encoded and URI encoded correctly
        captureException(err, {
          extra: {
            searchQuery: omit(props.location.query, 'email'),
          }
        })
      }
    }

    return params
  }),
  withState('forceShowLoading', 'setForceShowLoading', true),
  withState('cantUnsub', 'setCantUnsub', false),
  withState('cantResub', 'setCantResub', false),
  lifecycle({
    componentWillMount() {
      if (!this.props.isGeolocationLoaded && !this.props.isFetchGeolocationPending) {
        this.props.actions.fetchGeolocation()
      }

      if (this.props.embedNotFetched) {
        this.props.actions.fetchEmbedById(this.props.embedId)
      }
      setTimeout(() => this.props.setForceShowLoading(false), 1500)
    }
  }),
  withState('emailInput', 'setEmailInput', props => props.email),
  withState('unsubscribeChoice', 'setUnsubscribeChoice', null),
  withProps(props => {
    const unsubPending = props.unsubscribePending || (props.unsubscribeChoice === unsubscribeChoices.UNSUB_AND_REQUEST_DELETE && props.unsubRequestDeleteStatus === EndUserActions.requestUnsubDeleteDataPending.toString())
    return {
      handleResponseClick: (reason) => () => {
        props.actions.unsubscribe({ endUserId: props.endUserId, reason, email: props.emailInput })
        props.setResponded(true)
      },
      showLoading: props.forceShowLoading || props.embedFetchPending || props.isFetchGeolocationPending,
      confirmDisabled: unsubPending || (props.isGdprConsentRequired && props.unsubscribeChoice === null),
      unsubRequestDeleteSuccess: props.unsubRequestDeleteStatus === EndUserActions.requestUnsubDeleteDataSuccess.toString(),
      unsubPending: props.unsubscribePending || (props.unsubscribeChoice === unsubscribeChoices.UNSUB_AND_REQUEST_DELETE && props.unsubRequestDeleteStatus === EndUserActions.requestUnsubDeleteDataPending.toString()),
      requestDeleteFailed: props.unsubRequestDeleteStatus === EndUserActions.requestUnsubDeleteDataFailed.toString(),
    }
  }),
  withHandlers({
    onEmailConfirmChange: props => e => {
      props.setEmailInput(e.target.value)
    },
    onUnsubChoiceChange: props => choice => {
      props.setUnsubscribeChoice(choice)
    },
    onConfirmClick: props => () => {
      if (props.unsubscribeChoice === unsubscribeChoices.UNSUB_AND_REQUEST_DELETE) {
        props.actions.requestDeleteData({ orgId: props.orgId, email: props.emailInput, endUserId: props.endUserId })
        props.setUnsubscribe(true)
      }
      if (!!props.endUserId) {
        props.actions.unsubscribeByEncodedEmail({ email: props.emailInput, endUserId: props.endUserId })
        props.setUnsubscribe(true)
      } else if (!!props.emailInput && !!props.orgId) {
        props.actions.unsubscribeByEmailAndOrg({ email: props.emailInput, orgId: props.orgId })
        props.setUnsubscribe(true)
      } else {
        props.setCantUnsub(true)
        captureException('Need endUserId OR (orgId AND email) to unsubscribe', {
          extra: {
            orgId: props.orgId,
            endUserId: props.endUserId,
            emailInput: props.emailInput,
          }
        })
      }
    },
    onResubscribeClick: props => () => {
      if (!!props.endUserId) {
        props.actions.resubscribe({ email: props.emailInput, endUserId: props.endUserId })
        props.setResubscribe(true)
        props.setUnsubscribe(false)
      } else if (!!props.emailInput && !!props.orgId) {
        props.actions.resubscribeByEmailandOrgId({ email: props.emailInput, orgId: props.orgId })
        props.setResubscribe(true)
        props.setUnsubscribe(false)
      } else {
        props.setUnsubscribe(true)
        props.setCantResub(true)
        props.setCantUnsub(false)
        captureException('Need endUserId and email, or email and orgId to resubscribe', {
          extra: {
            endUserId: props.endUserId,
            emailInput: props.emailInput,
            orgId: props.orgId
          }
        })
      }
    }
  }),
  branch(props => props.showLoading, renderComponent(Loading))
)(Confirm)



// WEBPACK FOOTER //
// ./src/scripts/components/customer/Unsubscribe/index.js